export const getScreenBox = (start, end) => {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    return {
        minX,
        minY,
        maxX,
        maxY,
        width: Math.max(1, maxX - minX),
        height: Math.max(1, maxY - minY),
    };
};

export const getCircleData = (start, end) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;

    return {
        centerX: start.x,
        centerY: start.y,
        radius: Math.max(1, Math.sqrt(dx * dx + dy * dy)),
    };
};

export const createRectangleGeoJson = (map, start, end) => {
    if (!map || !start || !end) {
        return { type: "FeatureCollection", features: [] };
    }

    const box = getScreenBox(start, end);

    const p1 = map.unproject([box.minX, box.minY]);
    const p2 = map.unproject([box.maxX, box.minY]);
    const p3 = map.unproject([box.maxX, box.maxY]);
    const p4 = map.unproject([box.minX, box.maxY]);

    return {
        type: "FeatureCollection",
        features: [{
            type: "Feature",
            properties: {},
            geometry: {
                type: "Polygon",
                coordinates: [[
                    [p1.lng, p1.lat],
                    [p2.lng, p2.lat],
                    [p3.lng, p3.lat],
                    [p4.lng, p4.lat],
                    [p1.lng, p1.lat],
                ]],
            },
        }],
    };
};

export const createCircleGeoJson = (map, start, end, segments = 96) => {
    if (!map || !start || !end) {
        return { type: "FeatureCollection", features: [] };
    }

    const { centerX, centerY, radius } = getCircleData(start, end);
    const coordinates = [];

    for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;
        const lngLat = map.unproject([x, y]);

        coordinates.push([lngLat.lng, lngLat.lat]);
    }

    return {
        type: "FeatureCollection",
        features: [{
            type: "Feature",
            properties: {},
            geometry: {
                type: "Polygon",
                coordinates: [coordinates],
            },
        }],
    };
};

export const uniquePickedFeatures = (pickedObjects = []) => {
    const selected = new Map();

    pickedObjects.forEach(item => {
        const object = item?.object;
        if (!object?.id) return;
        selected.set(object.id, object);
    });

    return Array.from(selected.values());
};

export const filterSelectableFeatures = (features = []) => {
    const disallowedPrefixes = ['pgsql', 'kml', 'gml', 'geojson', 'shp', 'las', 'dx'];

    return features.filter(feature =>
        feature?.id &&
        !disallowedPrefixes.some(prefix => feature.id.startsWith(prefix))
    );
};

export const pickFeaturesInRectangle = (deckRef, start, end) => {
    if (!deckRef.current?.pickObjects || !start || !end) return [];

    const box = getScreenBox(start, end);

    const picked = deckRef.current.pickObjects({
        x: box.minX,
        y: box.minY,
        width: box.width,
        height: box.height,
    });

    return filterSelectableFeatures(uniquePickedFeatures(picked));
};

const flattenCoordinates = (coordinates) => {
    if (!Array.isArray(coordinates)) return [];

    if (
        typeof coordinates[0] === "number" &&
        typeof coordinates[1] === "number"
    ) {
        return [coordinates];
    }

    return coordinates.flatMap(flattenCoordinates);
};

export const pickFeaturesInCircle = (deckRef, start, end) => {
    if (!deckRef.current?.pickObjects || !start || !end) return [];

    const { centerX, centerY, radius } = getCircleData(start, end);

    const picked = deckRef.current.pickObjects({
        x: Math.floor(Math.max(0, centerX - radius)),
        y: Math.floor(Math.max(0, centerY - radius)),
        width: Math.ceil(Math.max(1, radius * 2)),
        height: Math.ceil(Math.max(1, radius * 2)),
    });

    return filterSelectableFeatures(uniquePickedFeatures(picked));
};